/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { LISTING_STATUS } from './constants/listingStatus';
import { updateListingVariantInState } from './helpers/channelHelpers';
import {
  findListingVariantEntity,
  getListingProductIdFromState,
} from './helpers/entityReadHelpers';
import { upsertListingVariant } from './helpers/entityAdapter';
import openDraftProductRecord from './helpers/openDraftProductRecord';
import refreshListingAssetsForChannels from './helpers/refreshListingAssetsForChannels';
import { setActiveTabByProductChannel, setSelectedChannelByProduct } from './helpers/uiAdapter';

export const createVariantDraftFromLive = createAsyncThunk(
  'products/createVariantDraftFromLive',
  async ({ productId, channelId, variantId }, { dispatch, getState, extra: { sdk } }) => {
    const productsState = getState().products;
    const listingProductId = getListingProductIdFromState(productsState, productId, channelId);
    const variantChannelEntry = findListingVariantEntity(productsState, variantId, channelId);
    const live = variantChannelEntry?.live;

    if (!listingProductId || !variantChannelEntry || !live) {
      throw new Error('Live variant not found');
    }

    const { data } = await sdk.products.createDraft(listingProductId, {
      source: 'live',
      masterVariantIds: [variantId],
      copyLiveAssets: true,
      replaceExistingDraftAssets: true,
    });

    const updatedVariant = (data.variants || []).find(
      ({ masterVariantId }) => String(masterVariantId) === String(variantId),
    );

    if (!updatedVariant) {
      throw new Error('Live variant not found');
    }

    await refreshListingAssetsForChannels({
      dispatch,
      getState,
      productId,
      channelIds: [channelId],
    });

    openDraftProductRecord({ productId, variantId });

    return { productId, channelId, updatedVariant };
  },
);

export const reducer = (builder) => {
  builder
    .addCase(createVariantDraftFromLive.pending, (state) => {
      state.isBusy = true;
      state.error = null;
    })
    .addCase(createVariantDraftFromLive.fulfilled, (state, { payload }) => {
      const { productId, channelId, updatedVariant } = payload;
      state.isBusy = false;
      updateListingVariantInState(state, { masterProductId: productId, channelId, data: updatedVariant });
      setSelectedChannelByProduct(state, productId, channelId);
      setActiveTabByProductChannel(state, productId, channelId, LISTING_STATUS.DRAFT);

      if (!updatedVariant?._id) return;

      upsertListingVariant(state, {
        ...(state.entities?.listingVariants?.[updatedVariant._id] || {}),
        ...updatedVariant,
      });
    })
    .addCase(createVariantDraftFromLive.rejected, (state, { error }) => {
      state.isBusy = false;
      state.error = error?.message || 'Cannot create variant staging from live';
    });
};
